// src/pages/Dashboard/components/DashboardTransactions.js

import React from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaClock as FaPending,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

// Co-locating TransactionRow here
const TransactionRow = ({ transaction, index }) => {
  const formatDate = (timestamp) => {
    let date;
    if (timestamp && typeof timestamp.toDate === "function") {
      date = timestamp.toDate();
    } else if (timestamp && timestamp.seconds) {
      date = new Date(
        timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000
      );
    } else {
      date = new Date(timestamp);
    }
    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed":
        return <FaCheckCircle className="text-green-500" />;
      case "pending":
        return <FaPending className="text-yellow-500" />;
      case "cancelled":
        return <FaExclamationTriangle className="text-red-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return `px-3 py-1 rounded-full text-xs font-medium ${
      badges[status] || badges.pending
    }`;
  };

  const { date, time } = formatDate(transaction.createdAt);
  const serviceName =
    transaction.services?.[0]?.serviceName || "Service";
  const customerName = transaction.scannedUser
    ? `${transaction.scannedUser.firstName} ${transaction.scannedUser.lastName}`
    : "Customer";

  return (
    <div
      className={`grid grid-cols-4 gap-4 py-4 px-4 hover:bg-gray-50 rounded-lg transition-colors ${
        index > 0 ? "border-t border-gray-100" : ""
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
        <div>
          <span className="text-gray-900 font-medium block">{date}</span>
          <span className="text-xs text-gray-500">{time}</span>
        </div>
      </div>
      <div>
        <span className="text-gray-700 font-medium block">{serviceName}</span>
        <span className="text-xs text-gray-500">{customerName}</span>
      </div>
      <div className="flex items-center space-x-2">
        {getStatusIcon(transaction.status)}
        <span className={getStatusBadge(transaction.status)}>
          {transaction.status}
        </span>
      </div>
      <span className="text-gray-900 font-semibold">
        ${transaction.totalAmount}
      </span>
    </div>
  );
};

const DashboardTransactions = ({ recentTransactions, transactionsLoading }) => {
  const navigate = useNavigate();

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900 flex items-center">
          <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full mr-3"></div>
          Recent Transactions
        </h3>
        <button
          className="text-blue-600 hover:text-blue-700 font-medium text-sm hover:bg-blue-50 px-3 py-1 rounded-lg transition-colors"
          onClick={() => navigate("/transactions")}
        >
          View All
        </button>
      </div>

      {transactionsLoading ? (
        <div className="space-y-3 p-4">{/* ... Shimmer loading ... */}</div>
      ) : recentTransactions.length > 0 ? (
        <div className="space-y-1">
          <div className="grid grid-cols-4 gap-4 py-3 px-4 text-sm font-medium text-gray-500 border-b border-gray-200">
            <span>Date & Time</span>
            <span>Service</span>
            <span>Status</span>
            <span>Amount</span>
          </div>
          {recentTransactions.map((transaction, index) => (
            <TransactionRow
              key={transaction.id}
              transaction={transaction}
              index={index}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8">{/* ... No transactions ... */}</div>
      )}
    </section>
  );
};

export default DashboardTransactions;